import { Box, Typography, useTheme, Button, InputBase, MenuItem, TextField, Autocomplete } from "@mui/material";
import { useNavigate }  from "react-router-dom";
import { useSelector }  from "react-redux";
import { useState }     from "react";
import moment           from "moment";

import ReceiptLongIcon  from "@mui/icons-material/ReceiptLong";
import SearchIcon       from "@mui/icons-material/Search";
import AddIcon          from "@mui/icons-material/Add";
import FilterListIcon   from "@mui/icons-material/FilterList";
import VisibilityIcon   from "@mui/icons-material/Visibility";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PeopleIcon       from "@mui/icons-material/People";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FixedSizeDialog   from "components/fixedSizeDialog";
import PayslipViewForm   from "../forms/payslipForms/payslipViewForm";
import { usePayslipsList } from "api/hooks/humanResource";
import { requester }     from "api/pesabackend";
import { currencyFormatter, withoutSymbolFormatter } from "utilities/currencyFormatter";

const PERIODS = ["THIS MONTH", "CUSTOM"];

export default function PayslipTable({ setIsSuccess }) {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { payslipList, isLoadingPayslipList, setSearch, setFilter } = usePayslipsList(1, business_branch_id);

  const [open,            setOpen]            = useState(false);
  const [openView,        setOpenView]        = useState(false);
  const [viewData,        setViewData]        = useState({});
  const [employeeList,    setEmployeeList]    = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectPayslipFor, setSelectPayslipFor] = useState("");
  const [search,          setSearchLocal]     = useState("");
  const [showCustom,      setShowCustom]      = useState(false);
  const [activePeriod,    setActivePeriod]    = useState("THIS MONTH");
  const [startDate,       setStartDate]       = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate,         setEndDate]         = useState(moment().endOf("month").format("YYYY-MM-DD"));

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: "13px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "13px" },
  };

  const fetchEmployeeList = async (search) => {
    try {
      const url = `/human_resource/employees?business_branch=${business_branch_id}&departmentFilter=all`;
      const res = await requester.get(search ? `${url}&search=${search}` : url);
      setEmployeeList(res.data.data);
    } catch {}
  };

  const handleOpenPayslip = async (employee) => {
    const res = await requester.get(`/human_resource/employees/payroll?payroll_type=all&employee=${employee}`);
    if (res.status === 200) navigate("/payslipForm", { state: { id: employee, payslipType: selectPayslipFor, data: res.data.data } });
  };

  const handleClickView = async (id) => {
    const res = await requester.get(`/human_resource/payslip/get/${id}`);
    if (res.status === 200) { setViewData(res.data.data); setOpenView(true); }
  };

  const handleClickEditOpen = async (id) => {
    const res = await requester.get(`/human_resource/payslip/get/${id}?view=edit`);
    if (res.status === 200) navigate("/editPayslipForm", { state: res.data.data });
  };

  const columns = [
    { label: "Payroll No."             },
    { label: "Payment Date"            },
    { label: "Employee"                },
    { label: "Renumeration", align: "right" },
    { label: "Allowance",    align: "right" },
    { label: "Deduction",    align: "right" },
    { label: "Net Total",    align: "right" },
    { label: "Created By"              },
    { label: "Actions",      align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <ReceiptLongIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>Payslips</Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>{payslipList.length} total records</Typography>
          </Box>
        </Box>
        <Button onClick={() => { fetchEmployeeList(null); setOpen(true); }} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
          Create Payslip
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", p: "0.75rem 1rem", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px" }}>
        <FilterListIcon sx={{ fontSize: "16px", color: accent }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", px: "0.75rem", height: "36px", backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f9f9f9", border: `1px solid ${border}`, borderRadius: "8px", flex: "1 1 180px", maxWidth: "220px" }}>
          <SearchIcon sx={{ fontSize: "16px", color: textSec }} />
          <InputBase placeholder="Search employee…" value={search} onChange={(e) => setSearchLocal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setSearch(search); }} sx={{ flex: 1, fontSize: "13px", color: textPri }} />
          <Box onClick={() => setSearch(search)} sx={{ fontSize: "11px", fontWeight: 500, color: accent, cursor: "pointer" }}>Search</Box>
        </Box>
        <Box sx={{ display: "flex", gap: "5px" }}>
          {PERIODS.map((p) => (
            <Box key={p} onClick={() => { setActivePeriod(p); if (p === "THIS MONTH") { setStartDate(moment().startOf("month").format("YYYY-MM-DD")); setEndDate(moment().endOf("month").format("YYYY-MM-DD")); setShowCustom(false); } else setShowCustom(true); }}
              sx={{ px: "0.65rem", py: "0.25rem", borderRadius: "20px", fontSize: "12px", fontWeight: 500, cursor: "pointer", border: `1px solid ${activePeriod === p ? accent : border}`, color: activePeriod === p ? accent : textSec, backgroundColor: activePeriod === p ? "rgba(29,158,117,0.1)" : "transparent", transition: "all 0.15s" }}>
              {p === "THIS MONTH" ? "This month" : "Custom"}
            </Box>
          ))}
        </Box>
        {showCustom && (
          <>
            <TextField label="Start date" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: border }, width: "150px" }} />
            <TextField label="End date" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: border }, width: "150px" }} />
          </>
        )}
        <Button onClick={() => setFilter({ start: startDate, end: endDate })} sx={{ fontSize: "12px", fontWeight: 500, px: "1rem", py: "0.35rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>Apply</Button>
      </Box>

      <ReportTable columns={columns} rows={payslipList} loading={isLoadingPayslipList}
        emptyMessage="No payslips found for this period"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.id}</Typography></RCell>
            <RCell>{moment(row.payment_date).format("LL")}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.related_employee.first_name} {row.related_employee.last_name}</Typography></RCell>
            <RCell align="right">{withoutSymbolFormatter(parseFloat(row.total_renumeration))}</RCell>
            <RCell align="right">{withoutSymbolFormatter(parseFloat(row.total_allowance))}</RCell>
            <RCell align="right">{withoutSymbolFormatter(parseFloat(row.total_deduction))}</RCell>
            <RCell align="right"><Typography sx={{ fontSize: "13px", fontWeight: 500, color: accent }}>{currencyFormatter(parseFloat(Number(row.total_renumeration) + Number(row.total_allowance) - Number(row.total_deduction)))}</Typography></RCell>
            <RCell>{row.added_by.first_name} {row.added_by.last_name}</RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                <Box onClick={() => handleClickView(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(29,158,117,0.3)", cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                  <VisibilityIcon sx={{ fontSize: "14px" }} /> View
                </Box>
                <Box onClick={() => handleClickEditOpen(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(186,117,23,0.3)", cursor: "pointer", color: "#BA7517", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(186,117,23,0.08)" } }}>
                  <EditOutlinedIcon sx={{ fontSize: "14px" }} /> Edit
                </Box>
              </Box>
            </RCell>
          </RRow>
        )}
      />

      {/* Create payslip dialog */}
      <FixedSizeDialog open={open} handleClose={() => { setOpen(false); setSelectedEmployee(null); setSelectPayslipFor(""); }} dialogHeading="Create Payslip" negativeResponse="Cancel" maxWidth="sm" scroll="body"
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", p: "0.875rem 1rem", backgroundColor: "rgba(29,158,117,0.07)", border: `1px solid rgba(29,158,117,0.2)`, borderRadius: "8px" }}>
              <PeopleIcon sx={{ fontSize: "18px", color: accent, flexShrink: 0, mt: "1px" }} />
              <Typography sx={{ fontSize: "13px", color: isDark ? "#9FE1CB" : "#0F6E56", lineHeight: 1.6 }}>Search for the employee and select the payslip type you want to record.</Typography>
            </Box>
            <Autocomplete
              onOpen={() => fetchEmployeeList(null)}
              onChange={(_, value) => setSelectedEmployee(value ? value.id : null)}
              options={employeeList}
              getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.id}>
                  <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{o.first_name} {o.last_name}</Typography>
                    <Typography sx={{ fontSize: "11px", color: textSec }}>{o.phone_number}</Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Employee name" onChange={(e) => fetchEmployeeList(e.currentTarget.value)}
                  sx={{ "& .MuiInputBase-root": { fontSize: "13px", borderRadius: "8px" }, "& .MuiInputLabel-root": { fontSize: "13px" } }} />
              )}
            />
            <TextField label="Payslip type" size="small" select value={selectPayslipFor} onChange={(e) => setSelectPayslipFor(e.target.value)} sx={{ ...inputSx }}>
              <MenuItem value="renumeration">Salaries and Deductions</MenuItem>
              <MenuItem value="allowance">Allowances</MenuItem>
            </TextField>
            <Button fullWidth disabled={!selectedEmployee || !selectPayslipFor} onClick={() => handleOpenPayslip(selectedEmployee)}
              sx={{ fontSize: "13px", fontWeight: 500, py: "0.6rem", borderRadius: "8px", textTransform: "none", backgroundColor: accent, color: "#fff", "&:hover": { backgroundColor: "#0F6E56" }, "&:disabled": { opacity: 0.5 } }}>
              Continue to Payslip Form
            </Button>
          </Box>
        }
      />

      <FixedSizeDialog open={openView} handleClose={() => setOpenView(false)} dialogHeading="Payslip Details" negativeResponse="Close" maxWidth="md" scroll="body"
        content={<PayslipViewForm viewData={viewData} />}
      />
    </Box>
  );
}